import BaseListPresenter from "../../../base/BaseListPresenter";
import { ServiceRecordLeave, formatCurrency } from "../../../components/common/Converter";
import * as XLSX from "xlsx";
import AggregateUseCase from "../../../usecases/object/AggregateUseCase";
import { getEmployeePipeline } from "./employeePipeline";
import { normalizeHeader, parseCSVText, toNumber, formatPeso, getAbbreviation, processWithConcurrencyLimit } from "./helpers/helpers";

class SalaryGradePresenter extends BaseListPresenter {
 constructor(
   view,
   findObjectUseCase,
   countObjectUseCase,
   deleteObjectUseCase,
   upsertUseCase,
   exportCSVUseCase,
   addSchemaUseCase,
   updateSchemaUseCase,
   deleteSchemaUseCase
 ) {
   super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
   this.upsertUseCase = upsertUseCase;
   this.exportCSVUseCase = exportCSVUseCase;
   this.addSchemaUseCase = addSchemaUseCase;
   this.updateSchemaUseCase = updateSchemaUseCase;
   this.deleteSchemaUseCase = deleteSchemaUseCase;
   this.view = view;
   this.aggregateUseCase = new AggregateUseCase();
 }
 init() {
   this.limit = 10;
   this.where = {};
   this.search = {};
   this.filter = {};
   this.include = ["all"];
   this.keys = undefined; // if keys are specified, only those keys will be returned
   this.sort = { createdAt: -1 };
   this.progress = true;
   this.reset();
 }

 _resetProgressDialog() {
   this.view.setState({ 
     showProgressModal: false, 
     saveProgress: 0,
     savedCount: 0,
     totalCount: 0
   });
 }

 createQuery() {
   const skip = (this.current - 1) * this.limit;
   const query = {
     limit: this.limit,
     skip: skip,
     where: { ...this.where, ...this.search, ...this.filter },
     include: this.include,
   };
   if (this.sort) {
     query.sort = this.sort;
   }
   const keys = this.keys || this.view.getKeys() || [];
   if (keys.length > 0) {
     query.keys = keys;
   }
   return query;
 }

 async findObjects() {
   const collection = this.view.getCollectionName();
   const query = this.createQuery();
   try {
     this.showProgress();
     this.findObjectUseCase.abort();
     const objects = await this.findObjectUseCase.execute(collection, query);
     this.objects = this.objects.concat(objects);
     this.view.setObjects(objects);
     this.view.setCurrent(this.current);
     this.hideProgress();
   } catch (error) {
     this.hideProgress();
     this.view.showError(error);
   }
   this.progress = false;
 }

 setCurrentPage(page) {
   this.current = page;
   this.documents = [];
   this.view.setCurrent(page);
   this.view.setObjects([]);
   this.findObjects();
 }

 _mapSalaryGradeRows = ({ headers, dataRows }) => {

  const normalizedHeaders = headers.map(normalizeHeader);
   const indexByHeader = new Map();
   normalizedHeaders.forEach((h, idx) => {
     if (!indexByHeader.has(h)) indexByHeader.set(h, idx);
   });

   const mapped = dataRows
     .map((row) => {
       const year = row[indexByHeader.get("YEAR")] ?? "";
       const salaryCode = row[indexByHeader.get("SALARY CODE")] ?? "";
       const name = row[indexByHeader.get("NAME")] ?? "";
       const sg = row[indexByHeader.get("SG")] ?? "";
       const step = row[indexByHeader.get("STEP")] ?? "";
       const monthlyRate = row[indexByHeader.get("MONTHLY RATE")] ?? "";
       const formattedRate = `₱ ${formatCurrency((monthlyRate ?? 0).toString())}`;

       const obj = {
         Year: year !== "" ? String(year).trim() : "",
         salaryCode: salaryCode !== "" ? String(salaryCode).trim() : "",
         name: name !== "" ? String(name).trim() : "",
         "Salary Grade": sg !== "" ? String(sg).trim() : "",
         Step: step !== "" ? String(step).trim() : "",
         "Monthly Rate": formattedRate,
       };

       const hasAny = Object.values(obj).some(
         (v) => v !== "" && v !== null && v !== undefined
       );
       return hasAny ? obj : null;
     })
     .filter(Boolean);

   return {

     data: mapped,
   };
 };

 async handleImportCSV() {
  const { rawExcelData, employees } = this.view.state;
   const rows = rawExcelData?.data || [];

   if (!rows.length) {
     this.view.showError("Please upload a file first.");
     return;
   }

   try {

    const totalTasks =  rows.length;

    this.view.setState({
      showProgressModal: true,
      saveProgress: 0,
      savedCount: 0,
      totalCount: totalTasks
    });

    let processedCount = 0;
    const updateProgress = () => {
      processedCount++;
      const progress = Math.round((processedCount / totalTasks) * 100);
      this.view.setState({
        saveProgress: progress,
        savedCount: processedCount
      });
    };

    const employeesBySalaryCode = new Map();
    for (const employee of employees) {
      const code = employee.salaryCode;
      const existing = employeesBySalaryCode.get(code);
      if (!existing) {
        employeesBySalaryCode.set(code, [employee]);
      } else {
        existing.push(employee);
      }
    }

    const employeesByRow = rows.map(row => {
      const employees = employeesBySalaryCode.get(row.salaryCode) || [];
      employees.sgName = row.name;
      employees.sgMonthlyRate = row["Monthly Rate"];
      return employees;
    });

    if (employeesByRow.length === 0) {
      return;
    }

    const allEmployeeTasks = [];
    for (const employeeArray of employeesByRow) {
      for (const item of employeeArray) {
        allEmployeeTasks.push(
          this.insertServiceRecord(item, employeeArray.sgName, employeeArray.sgMonthlyRate)
            .catch(error => {
              console.error(`Error processing employee ${item.id}:`, error);
              return null; 
            })
        );
      }
    }

    const salaryGradeTasks = rows.map(row =>
      this.upsertUseCase.execute("salary_grade", row)
        .then(() => {
          updateProgress();
          return null;
        })
        .catch(error => {
          console.error(`Error upserting salary grade for ${row.salaryCode}:`, error);
          updateProgress();
          return null;
        })
    );

    try {
      await Promise.all([
        processWithConcurrencyLimit(allEmployeeTasks, 5),
        Promise.all(salaryGradeTasks)
      ]);

      this.view.setState({ saveProgress: 100, savedCount: totalTasks });

      setTimeout(() => {
        this._resetProgressDialog();
        this.view.showSuccess("Salary grades imported successfully.");
        this.view.refetchData();
      }, 500);
    } catch (error) {
      this._resetProgressDialog();
      throw error;
    }
   } catch (e) {
     console.error("Error importing salary grades", e);
     this._resetProgressDialog();
     this.view.showError("Error importing salary grades. Please try again.");
   }
 }

 async insertServiceRecord(item, sgName, sgMonthlyRate) {
  try {
    const effectivity = item?.effectivityDate
      ? new Date(item?.effectivityDate)
      : new Date();

    const serviceRecord = await this.findObjectUseCase.execute("service_record", {
      where: { empID: item?.id, to: { $exists: false } },
    });

    const dateFrom = serviceRecord[0]?.from
      ? new Date(serviceRecord[0]?.from)
      : new Date();
    dateFrom.setDate(dateFrom.getDate() - 1);

    const leaveRequests = await this.findObjectUseCase.execute("leave_request", {
      where: {
        employee: { id: item.id },
        stats: { $ne: "For Processing" },
        $or: [
          { startdate: { $gte: dateFrom, $lte: effectivity } },
          { enddate: { $gte: dateFrom, $lte: effectivity } },
        ],
      },
    });

    const monthly = toNumber(sgMonthlyRate || "");
    const annual = monthly * 12;
    const salary = `${sgName || ""} (${formatPeso(annual)} per month)`;

    const serviceLog = {
      email: item?.email,
      empID: item?.id,
      from: effectivity,
      employee: { id: item.id },
      designation: item?.position?.position_title,
      status: item?.employmentType?.name || "",
      salary,
      station: "BUREAU OF IMMIGRATION",
      branch: "NATIONAL",
      cause: "Notice of Salary Adjustment",
    };

    if (serviceRecord.length > 0) {
      const leaveStatus = leaveRequests.map(leave => {
        const abbreviation = getAbbreviation(leave.selectedType);
        return ServiceRecordLeave(
          abbreviation,
          new Date(leave?.startdate),
          new Date(leave?.enddate)
        );
      });

      serviceRecord[0].to = serviceLog.from;
      serviceRecord[0].leave = leaveStatus;
      serviceRecord[0].status = serviceLog.status;

      await this.upsertUseCase.execute("service_record", serviceRecord[0]);
    }
    await this.upsertUseCase.execute("service_record", serviceLog);
  } catch (error) {
    console.error("Error in insertServiceRecord:", error);
    throw error;
  }
}

 handleExcelUpload = (event) => {
   const file = event.target.files[0];

   if (!file) return;

   const reader = new FileReader();
   const lowerName = file.name?.toLowerCase?.() || "";

   if (lowerName.endsWith(".csv")) {
     reader.onload = (e) => {
       const text = e.target.result;
       const { headers, dataRows } = parseCSVText(text);
       const mapped = this._mapSalaryGradeRows({ headers, dataRows });
       this.view.setState({ rawExcelData: mapped });
     };
     reader.readAsText(file);
     return;
   }

   reader.onload = async (e) => {
     const data = new Uint8Array(e.target.result);
     const workbook = XLSX.read(data, { type: "array" });

     const sheetName = workbook.SheetNames[0];
     const worksheet = workbook.Sheets[sheetName];
     const sheetRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
     const headers = (sheetRows?.[0] || []).map((h) => (h ?? "").toString().trim());
     const dataRows = (sheetRows || []).slice(1);

     const mapped = this._mapSalaryGradeRows({ headers, dataRows });
     const employees = await this.fetchEmployees(mapped?.data);
     this.view.setState({ rawExcelData: mapped, employees });
   };

   reader.readAsArrayBuffer(file);
 };

 async fetchEmployees(data) {
  const uniqueSalaryCodes = [...new Set(data.map(row => row.salaryCode).filter(Boolean))];

  const pipeline = getEmployeePipeline(uniqueSalaryCodes);
  return await this.aggregateUseCase.execute("employees", pipeline);
 }


 onChangeFilter(type, value, field) {
   const where = { ...this.view.state.filters };

   switch (type) {
     case "Pointer":
       if (Object.keys(value).length > 0) {
         where[field] = { id: value.id };
       } else {
         delete where[field]; // Remove filter if no value
       }
       break;
     case "Boolean":
       if (value !== null) {
         where[field] = value;
       } else {
         delete where[field]; // Remove filter if no value
       }
       break;
     default:
       if (value) {
         where[field] = { $regex: value, $options: "i" };
       } else {
         delete where[field]; // Remove filter if no value
       }
   }
   this.view.setState({ filters: where }, () => {
     this.filterSubmit(this.view.state.filters);
   });
 }
}
export default SalaryGradePresenter;

